import { useApolloClient } from "@apollo/client";
import { Capacitor } from "@capacitor/core";
import { LoadingSpinner } from "@msys/ui";
import { Button, Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../clients/graphqlTypes.js";
import { useLanguages } from "../../common/translations/useLanguages.js";
import { buildRegisterUrlWithPrefill } from "../auth/buildRegisterUrlWithPrefill.js";
import { LoginButton } from "../auth/LoginButton.js";
import { RegisterButton } from "../auth/RegisterButton.js";
import { INVITATION_SCOPE } from "../auth/signup/signupScope.js";
import { useAuth } from "../auth/useAuth.js";
import { useSelectedUser } from "../auth/useSelectedUser.js";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import { REGISTER_AFTER_LOGOUT_URL_SEARCH_PARAM } from "../constants.js";
import { OrganisationAvatar } from "../features/organisations/OrganisationAvatar.js";
import {
  ONBOARDING_OUTLINED_BUTTON_SX,
  ONBOARDING_PRIMARY_BUTTON_SX,
} from "../features/organisations/onboarding/onboardingDesign.js";
import { ButtonDivider } from "./ButtonDivider.js";
import { InvitationHeading } from "./InvitationHeading.js";
import { InvitationTokenData } from "./InvitationTokenData.js";
import { useAcceptOrganisationMembershipInvitationMutation } from "./OrganisationMembershipInvitation.generated.js";
import { useInvitationToken } from "./useInvitationToken.js";

interface Prefill {
  title: string;
  firstname: string;
  familyname: string;
  email: string;
}

// The token query resolves this field to either the pending invitation or
// the data-free `InvitationNoLongerOpen` member (see
// InvitationTokenProvider.gql) — never a settled invitation's detail.
type Invitation = Extract<
  InvitationTokenData,
  { __typename: "OrganisationMembershipInvitation" | "InvitationNoLongerOpen" }
>;

type OpenInvitation = Extract<
  Invitation,
  { __typename: "OrganisationMembershipInvitation" }
>;

interface Props {
  invitation: Invitation;
  isAuthenticated: boolean;
}

export const OrganisationMembershipInvitation = ({
  invitation,
  isAuthenticated,
}: Props) => {
  const { t } = useTranslate(["Global"]);
  const { currentLanguage } = useLanguages();
  const { setSelectedUserId } = useSelectedUser();
  const { remove: removeInvitationToken } = useInvitationToken();
  const { auth } = useAuth();

  const client = useApolloClient();
  const [acceptOrgmembershipInvitation] =
    useAcceptOrganisationMembershipInvitationMutation({
      client,
      refetchQueries: [namedOperations.Query.Me],
      awaitRefetchQueries: true,
    });

  // The union member, not a `status` field, is the source of truth — the
  // selection drops `status` because the backend only ever returns the
  // `OrganisationMembershipInvitation` member while it is PENDING.
  const pendingInvitation: OpenInvitation | null =
    invitation.__typename === "OrganisationMembershipInvitation"
      ? invitation
      : null;

  const authenticatedUserEmail = auth.idTokenParsed?.email;

  const authenticatedUserIsInvitee =
    pendingInvitation !== null &&
    authenticatedUserEmail !== undefined &&
    authenticatedUserEmail.toLowerCase() ===
      pendingInvitation.user.email.toLowerCase();

  const hasAccepted = React.useRef(false);

  const accept = React.useCallback(async () => {
    if (!pendingInvitation) return;
    const { data } = await acceptOrgmembershipInvitation({
      variables: {
        input: {
          token: pendingInvitation.invitationToken,
          locale: currentLanguage.locale,
        },
      },
    });
    if (!data) throw new Error("Failed to accept invitation!");
    removeInvitationToken();
    setSelectedUserId(data.acceptOrganisationMembershipInvitation.user.id);
  }, [
    acceptOrgmembershipInvitation,
    currentLanguage.locale,
    pendingInvitation,
    removeInvitationToken,
    setSelectedUserId,
  ]);

  React.useEffect(() => {
    if (
      pendingInvitation &&
      authenticatedUserIsInvitee &&
      !hasAccepted.current
    ) {
      hasAccepted.current = true;
      // React error boundaries do not catch rejected promises (see
      // docs/guides/frontend-error-boundaries.md); handle the rejection here
      // instead. No toast of our own — the Apollo error link already
      // renders the GraphQL error message for every failed operation, and a
      // second one here would report the same failure twice.
      accept().catch(error => {
        Sentry.captureException(error);
      });
    }
  }, [accept, authenticatedUserIsInvitee, pendingInvitation]);

  if (!pendingInvitation) {
    return (
      <Stack>
        <InvitationHeading>
          {t("Sorry, this invitation is no longer valid!", {
            ns: "Global",
          })}
        </InvitationHeading>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disableElevation
          sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "32px" }}
          onClick={() => {
            removeInvitationToken();
          }}
        >
          {t("Return to Meister Systems", { ns: "Global" })}
        </Button>
      </Stack>
    );
  }

  if (!isAuthenticated) {
    return (
      <OrganisationMembershipInvitationUnAuhenticated
        invitation={pendingInvitation}
      />
    );
  }

  if (!authenticatedUserIsInvitee) {
    return (
      <OrganisationMembershipInvitationInvalidAccount
        invitation={pendingInvitation}
      />
    );
  }

  return <LoadingSpinner />;
};

const OrganisationMembershipInvitationUnAuhenticated = ({
  invitation,
}: {
  invitation: OpenInvitation;
}) => {
  const { t } = useTranslate(["Global"]);
  const { auth } = useAuth();
  const { currentLanguage } = useLanguages();
  const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams();
  const registerAfterLogout = urlSearchParams.has(
    REGISTER_AFTER_LOGOUT_URL_SEARCH_PARAM
  );

  const prefill = React.useMemo(() => {
    return getPrefillData(invitation.user);
  }, [invitation.user]);

  const hasStartedRegistration = React.useRef(false);
  // Keeps the spinner up once the marker is gone, until the registration
  // page loads; only a failure shows the invitation again.
  const [isStartingRegistration, setIsStartingRegistration] =
    React.useState(false);
  React.useEffect(() => {
    if (!registerAfterLogout || hasStartedRegistration.current) return;
    hasStartedRegistration.current = true;
    setIsStartingRegistration(true);
    // Back from the wrong-account logout: continue straight to the invitee's
    // registration, returning to the invitation without the marker. The
    // native app keeps the Capacitor adapter's deep link.
    const redirectUri = new URL(window.location.href);
    redirectUri.searchParams.delete(REGISTER_AFTER_LOGOUT_URL_SEARCH_PARAM);
    setUrlSearchParams({}, [REGISTER_AFTER_LOGOUT_URL_SEARCH_PARAM], {
      replace: true,
    });
    auth
      .createRegisterUrl({
        locale: currentLanguage.locale,
        scope: INVITATION_SCOPE,
        ...(Capacitor.isNativePlatform()
          ? {}
          : { redirectUri: redirectUri.toString() }),
      })
      .then(registerUrl => {
        window.open(buildRegisterUrlWithPrefill(registerUrl, prefill), "_self");
      })
      .catch(error => {
        Sentry.captureException(error);
        setIsStartingRegistration(false);
      });
  }, [
    auth,
    currentLanguage.locale,
    prefill,
    registerAfterLogout,
    setUrlSearchParams,
  ]);

  if (registerAfterLogout || isStartingRegistration) return <LoadingSpinner />;

  return (
    <Stack sx={{ alignItems: "center" }}>
      <OrganisationAvatar
        organisationAvatar={invitation.organisation}
        size="xl"
      />
      <InvitationHeading sx={{ mt: "24px" }}>
        {t(
          "Please sign up or log in to join the {inviterOrgName} account on Meister Systems!",
          {
            ns: "Global",
            inviterOrgName: invitation.organisation.title,
          }
        )}
      </InvitationHeading>
      <Stack sx={{ width: "100%", mt: "32px" }}>
        <RegisterButton
          prefill={prefill}
          scope={INVITATION_SCOPE}
          color="primary"
          sx={ONBOARDING_PRIMARY_BUTTON_SX}
        />
        <ButtonDivider label={t("Or", { ns: "Global" })} />
        <LoginButton
          variant="outlined"
          color="primary"
          prefill={prefill}
          scope={INVITATION_SCOPE}
          sx={ONBOARDING_OUTLINED_BUTTON_SX}
        />
      </Stack>
    </Stack>
  );
};

const OrganisationMembershipInvitationInvalidAccount = ({
  invitation,
}: {
  invitation: OpenInvitation;
}) => {
  const { auth } = useAuth();
  const { t } = useTranslate(["Global"]);
  const { remove: removeInvitationToken } = useInvitationToken();
  const { setUrlSearchParams } = useUrlSearchParams();

  return (
    <Stack sx={{ alignItems: "center" }}>
      <OrganisationAvatar
        organisationAvatar={invitation.organisation}
        size="xl"
      />
      <InvitationHeading sx={{ mt: "24px" }}>
        {t(
          "You must be logged in with {inviteeEmail} to join the {inviterOrgName} account on Meister Systems",
          {
            ns: "Global",
            inviterOrgName: invitation.organisation.title,
            inviteeEmail: invitation.user.email,
          }
        )}
      </InvitationHeading>
      <Stack sx={{ width: "100%", mt: "32px" }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          sx={ONBOARDING_PRIMARY_BUTTON_SX}
          onClick={async () => {
            // Keycloak accepts only the client's app URLs as the post-logout
            // redirect, so the logout returns to this invitation, marked to
            // start the registration once signed out (see
            // OrganisationMembershipInvitationUnAuhenticated).
            try {
              if (Capacitor.isNativePlatform()) {
                // The native app returns through its own logout callback and
                // stays on this page.
                await auth.logout(undefined);
                setUrlSearchParams({
                  [REGISTER_AFTER_LOGOUT_URL_SEARCH_PARAM]: "1",
                });
                return;
              }
              const redirectUri = new URL(window.location.href);
              redirectUri.searchParams.set(
                REGISTER_AFTER_LOGOUT_URL_SEARCH_PARAM,
                "1"
              );
              await auth.logout({ redirectUri: redirectUri.toString() });
            } catch (e) {
              Sentry.captureException(e);
            }
          }}
        >
          {t("Log out", { ns: "Global" })}
        </Button>
        <ButtonDivider label={t("Or", { ns: "Global" })} />
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          sx={ONBOARDING_OUTLINED_BUTTON_SX}
          onClick={() => {
            removeInvitationToken();
          }}
        >
          {t("Return to Meister Systems", { ns: "Global" })}
        </Button>
      </Stack>
    </Stack>
  );
};

function getPrefillData(contact: Prefill) {
  const firstName = contact.firstname;
  const lastName = contact.familyname;
  const email = contact.email;
  const salutation = contact.title;

  return {
    firstName,
    lastName,
    email,
    salutation,
  };
}
